import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { Model, SuspensionKinematicsParams } from '@shared/types';
import { OutputFormat } from '@/components/OutputFormatSelector';
import type { TabToolbarState } from '@/types/tabTypes';
import {
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  GYROID_SPHERE_DEFAULTS,
} from '@/config/implicitPrimitives';
import type { DownloadFormat } from '@/components/download/DownloadTypes';
import { HomepageContext } from './homepage-context';

export type { TabToolbarState };

// Vehicle nav (JAWS / domain selection) is persisted directly to localStorage
// under a dedicated key so it survives a hard refresh, mirroring the
// featureTreeOpen ('tk-feature-tree') and outputFormat
// ('telekinesis:defaultOutputFormat') patterns below. Without this, the only
// channel carrying nav state across reloads is the per-tab toolbarState blob,
// which is captured too late (only on tab-switch/new-tab) and so loses JAWS on
// refresh. See issue #51.
const VEHICLE_NAV_KEY = 'tk-vehicle-nav';

interface VehicleNavState {
  vehicleEnabled: boolean;
  textToCadSelected: boolean;
  vehicleTab: string;
  vehicleSubTab: string;
}

const DEFAULT_VEHICLE_NAV: VehicleNavState = {
  vehicleEnabled: false,
  textToCadSelected: false,
  vehicleTab: 'space',
  vehicleSubTab: 'rocket-nozzle',
};

function loadVehicleNav(): VehicleNavState {
  try {
    const raw = localStorage.getItem(VEHICLE_NAV_KEY);
    if (!raw) return { ...DEFAULT_VEHICLE_NAV };
    const parsed = JSON.parse(raw) as Partial<VehicleNavState>;
    return {
      vehicleEnabled:
        typeof parsed.vehicleEnabled === 'boolean'
          ? parsed.vehicleEnabled
          : DEFAULT_VEHICLE_NAV.vehicleEnabled,
      textToCadSelected:
        typeof parsed.textToCadSelected === 'boolean'
          ? parsed.textToCadSelected
          : DEFAULT_VEHICLE_NAV.textToCadSelected,
      vehicleTab:
        typeof parsed.vehicleTab === 'string'
          ? parsed.vehicleTab
          : DEFAULT_VEHICLE_NAV.vehicleTab,
      vehicleSubTab:
        typeof parsed.vehicleSubTab === 'string'
          ? parsed.vehicleSubTab
          : DEFAULT_VEHICLE_NAV.vehicleSubTab,
    };
  } catch {
    // localStorage disabled or malformed JSON — fall back to defaults.
    return { ...DEFAULT_VEHICLE_NAV };
  }
}

/**
 * Which CAD-authoring mode the toolbar is in.
 * - `'automatic'` — today's AI-chat-generated pipeline (unchanged, the default).
 * - `'manual'` — the FreeCAD-style feature bar (Sketch/Pad/Pocket/Fillet/…),
 *   authored directly by the user with no LLM in the loop. Orthogonal to the
 *   vehicle-domain selection, so it lives here rather than as a sub-tab value.
 *   See docs/freecad-manual-cad-integration-plan.md.
 */
export type CadMode = 'automatic' | 'manual';

/** Viewer backdrop theme. Dark = mostly-black studio (bgShade 10); Light =
 *  mostly-white (bgShade 90). Just a preset flip of the existing viewer tint. */
export type ViewerTheme = 'dark' | 'light';

export interface TubeframeParams {
  od: string;
  id: string;
  angle: string;
  matingSurface: string;
  units: 'in' | 'mm';
}

export interface SupinParams {
  inletType: 'K1' | 'K2' | 'K3' | 'K4' | 'K5';
  // Freestream — Mach + exactly one of altitude (Kfs=1, ft) or qdyn
  // (Kfs=4, psf). Altitude is the typical case; qdyn is the hypersonic
  // K5 convention.
  mach: string;
  altitude?: string;
  qdyn?: string;
  // Engine corrected mass flow Wc2 (lbm/s). SUPIN sizes Acap to match.
  engineFlow: string;
  // K2 / K4 — 2D ramp stack
  rampStages?: string;
  rampAngle?: string;
  // K3 — axi-spike cone
  coneAngle?: string;
  numStages?: string;
  // K5 — Busemann parent flow Mach
  parentMach?: string;
  // Optional output toggle. Off by default; enabling adds 10-60s.
  cfdGrids?: boolean;
}

interface SidebarActionOpts {
  fn: (() => void) | null;
  enabled: boolean;
  loading?: boolean;
  stopFn?: (() => void) | null;
}

interface RegenActionOpts {
  fn: (() => void) | null;
  enabled: boolean;
  hasArtifact: boolean;
  loading?: boolean;
  stopFn?: (() => void) | null;
}

export interface HomepageContextValue {
  vehicleEnabled: boolean;
  setVehicleEnabled: (enabled: boolean) => void;
  textToCadSelected: boolean;
  setTextToCadSelected: (selected: boolean) => void;
  vehicleTab: string;
  setVehicleTab: (tab: string) => void;
  vehicleSubTab: string;
  setVehicleSubTab: (subTab: string) => void;
  // Manual (FreeCAD-style) vs Automatic (AI-chat) CAD authoring. Orthogonal to
  // the vehicle domain — Manual is available under any domain. Default
  // 'automatic' keeps today's pipeline untouched.
  cadMode: CadMode;
  setCadMode: (mode: CadMode) => void;
  // Viewer backdrop theme (dark/light). Drives the viewer tint via bgShade.
  viewerTheme: ViewerTheme;
  setViewerTheme: (theme: ViewerTheme) => void;
  tubeframeParamsReady: boolean;
  setTubeframeParamsReady: (ready: boolean) => void;
  tubeframeParams: TubeframeParams | null;
  setTubeframeParams: (params: TubeframeParams | null) => void;
  supinInletType: string;
  setSupinInletType: (type: string) => void;
  supinParamsReady: boolean;
  setSupinParamsReady: (ready: boolean) => void;
  supinParams: SupinParams | null;
  setSupinParams: (params: SupinParams | null) => void;
  suspensionKinematicsParamsReady: boolean;
  setSuspensionKinematicsParamsReady: (ready: boolean) => void;
  suspensionKinematicsParams: SuspensionKinematicsParams | null;
  setSuspensionKinematicsParams: (
    params: SuspensionKinematicsParams | null,
  ) => void;
  // Implicit Primitives — curated LEAP71 patterns. The active primitive picks
  // which template the backend runs; implicitPrimitiveParams carries the
  // current values of that primitive's parameters. Defaults match the LEAP71
  // example sources, so the moment a user picks a primitive they already
  // have a generatable shape.
  implicitPrimitive: ImplicitPrimitive;
  setImplicitPrimitive: (p: ImplicitPrimitive) => void;
  implicitPrimitiveParams: ImplicitPrimitiveParams;
  setImplicitPrimitiveParams: (p: ImplicitPrimitiveParams) => void;
  /** Reset implicit-primitive selection to the default Gyroid Sphere. This
   *  value is GLOBAL (one provider feeds every home tab's PromptView), so a
   *  freshly opened/last-closed home tab must reset it — otherwise a prior
   *  tab's NASA Unitcell Lattice Converter upload (kind 'lattice-fill' +
   *  previewUrl) bleeds the converter standby onto the New Tab page (#309). */
  resetImplicitPrimitiveParams: () => void;
  featureTreeOpen: boolean;
  setFeatureTreeOpen: (open: boolean) => void;
  // LLM + kernel — shared between sidebar and TextAreaChat
  model: Model;
  setModel: (model: Model) => void;
  outputFormat: OutputFormat;
  setOutputFormat: (format: OutputFormat) => void;
  type: 'parametric' | 'imagetocad';
  setType: (t: 'parametric' | 'imagetocad') => void;
  // Whether the prompt currently has an uploaded image. Published up from
  // TextToCadSection (which owns the image state) so the sidebar model dropdown
  // can lock to the image tiers when an image is attached.
  imageUploaded: boolean;
  setImageUploaded: (v: boolean) => void;
  // DOM slot at the bottom of the feature tree. It hosts the one
  // UniversalDownloadMenu; viewers no longer portal their own button in. Kept as
  // the signal for "is the sidebar footer host present?" — when null (feature
  // tree collapsed, or a route without the sidebar like /share), the active
  // viewer floats the same menu bottom-left instead.
  downloadSlot: HTMLElement | null;
  setDownloadSlot: (el: HTMLElement | null) => void;
  // The ONE download registry. The active viewer publishes the file types the
  // current model can export; the single UniversalDownloadMenu renders them.
  // Registration is owner-scoped (`ownerId`): passing `null` clears only if the
  // caller currently owns the slot, so an inactive tab can't wipe the active
  // model's download menu.
  downloadFormats: DownloadFormat[] | null;
  registerDownload: (ownerId: string, formats: DownloadFormat[] | null) => void;
  // DOM slot in the feature tree where the JAWS Suspension Kinematics workbench
  // portals its controls (Import Vehicle, Type/Axle/…, feature tree, Download),
  // so JAWS lives in the shared feature-tree sidebar instead of its own panel.
  jawsSlot: HTMLElement | null;
  setJawsSlot: (el: HTMLElement | null) => void;
  // Sidebar "Generate 3D Model" button registry
  sidebarGenerateEnabled: boolean;
  sidebarGenerateLoading: boolean;
  registerSidebarAction: (opts: SidebarActionOpts) => void;
  fireSidebarGenerate: () => void;
  fireSidebarStop: () => void;
  // Domain parameter change signalling (SpaceContent / LandContent → ParametricView)
  domainParamsVersion: number;
  bumpDomainParams: () => void;
  // Regen action registry (editor → FeatureTreeSidebar)
  hasArtifact: boolean;
  regenEnabled: boolean;
  regenLoading: boolean;
  registerRegenAction: (opts: RegenActionOpts) => void;
  fireRegen: () => void;
  fireRegenStop: () => void;
  isGenerating: boolean;
  // Generation timing (messageId → elapsed seconds)
  generationTimes: Record<string, number>;
  recordGenerationTime: (messageId: string, seconds: number) => void;
  // Per-tab state capture/restore
  captureTabState: () => TabToolbarState;
  loadTabState: (state: TabToolbarState) => void;
}

export function HomepageProvider({ children }: { children: ReactNode }) {
  // Lazily rehydrate vehicle nav from localStorage so JAWS (and any other
  // domain selection) survives a hard refresh. See VEHICLE_NAV_KEY above.
  const initialNav = useState(loadVehicleNav)[0];
  const [vehicleEnabled, setVehicleEnabled] = useState(
    initialNav.vehicleEnabled,
  );
  const [textToCadSelected, setTextToCadSelected] = useState(
    initialNav.textToCadSelected,
  );
  const [vehicleTab, setVehicleTab] = useState(initialNav.vehicleTab);
  const [vehicleSubTab, setVehicleSubTab] = useState(initialNav.vehicleSubTab);
  const [cadMode, setCadMode] = useState<CadMode>('automatic');
  const [viewerTheme, setViewerTheme] = useState<ViewerTheme>('dark');
  const [tubeframeParamsReady, setTubeframeParamsReady] = useState(false);
  const [tubeframeParams, setTubeframeParams] =
    useState<TubeframeParams | null>(null);
  const [supinInletType, setSupinInletType] = useState('');
  const [supinParamsReady, setSupinParamsReady] = useState(false);
  const [supinParams, setSupinParams] = useState<SupinParams | null>(null);
  const [suspensionKinematicsParamsReady, setSuspensionKinematicsParamsReady] =
    useState(false);
  const [suspensionKinematicsParams, setSuspensionKinematicsParams] =
    useState<SuspensionKinematicsParams | null>(null);
  const [implicitPrimitive, setImplicitPrimitive] =
    useState<ImplicitPrimitive>('gyroid-sphere');
  // Initialize with Gyroid Sphere's tutorial defaults so the sidebar has
  // valid numbers from first render — the user shouldn't have to fill in
  // empty fields just to see the canonical reference shape.
  const [implicitPrimitiveParams, setImplicitPrimitiveParams] =
    useState<ImplicitPrimitiveParams>({
      kind: 'gyroid-sphere',
      params: GYROID_SPHERE_DEFAULTS,
    });
  const [featureTreeOpen, setFeatureTreeOpen] = useState(
    () => localStorage.getItem('tk-feature-tree') !== 'false',
  );
  const [model, setModel] = useState<Model>('anthropic/claude-opus-4.8');
  const [outputFormat, setOutputFormatState] = useState<OutputFormat>(() => {
    try {
      const stored = localStorage.getItem('telekinesis:defaultOutputFormat');
      // 'mesh' intentionally NOT accepted — the Mesh kernel is commented out of
      // the selector; a stored 'mesh' falls back to 'solidbody'.
      if (stored === 'solidbody') return stored as OutputFormat;
    } catch {
      // localStorage may be disabled
    }
    return 'solidbody';
  });

  const setOutputFormat = useCallback((fmt: OutputFormat) => {
    setOutputFormatState(fmt);
    try {
      localStorage.setItem('telekinesis:defaultOutputFormat', fmt);
    } catch {
      // localStorage may be disabled
    }
  }, []);

  // Reset to the canonical default so a new/cleared home tab is a clean slate.
  // implicitPrimitiveParams is global (not part of per-tab toolbarState), so
  // without this a lattice-converter upload in one tab shows its standby on
  // every other home tab — including the New Tab page. See #309.
  const resetImplicitPrimitiveParams = useCallback(() => {
    setImplicitPrimitive('gyroid-sphere');
    setImplicitPrimitiveParams({
      kind: 'gyroid-sphere',
      params: GYROID_SPHERE_DEFAULTS,
    });
  }, []);
  const [type, setType] = useState<'parametric' | 'imagetocad'>('parametric');
  const [imageUploaded, setImageUploaded] = useState(false);
  const [downloadSlot, setDownloadSlot] = useState<HTMLElement | null>(null);
  const [jawsSlot, setJawsSlot] = useState<HTMLElement | null>(null);
  // The one download registry (see the interface). Owner-scoped so an inactive
  // viewer clearing on unmount can't wipe the active model's menu. Setter is
  // stable so useRegisterDownload's effect doesn't re-fire on identity churn.
  const [downloadRegistration, setDownloadRegistration] = useState<{
    ownerId: string;
    formats: DownloadFormat[];
  } | null>(null);
  const registerDownload = useCallback(
    (ownerId: string, formats: DownloadFormat[] | null) => {
      setDownloadRegistration((prev) => {
        if (formats && formats.length > 0) return { ownerId, formats };
        return prev?.ownerId === ownerId ? null : prev;
      });
    },
    [],
  );
  const downloadFormats = downloadRegistration?.formats ?? null;

  // Domain params version counter — bumped by SpaceContent/LandContent on any input change
  const [domainParamsVersion, setDomainParamsVersion] = useState(0);
  const bumpDomainParams = useCallback(() => {
    setDomainParamsVersion((v) => v + 1);
  }, []);

  // Sidebar "Generate 3D Model" button — use refs for callbacks to avoid stale closures
  const sidebarGenerateFnRef = useRef<(() => void) | null>(null);
  const sidebarStopFnRef = useRef<(() => void) | null>(null);
  const [sidebarGenerateEnabled, setSidebarGenerateEnabled] = useState(false);
  const [sidebarGenerateLoading, setSidebarGenerateLoading] = useState(false);

  // Regen action registry
  const regenFnRef = useRef<(() => void) | null>(null);
  const regenStopFnRef = useRef<(() => void) | null>(null);
  const [hasArtifact, setHasArtifact] = useState(false);
  const [regenEnabled, setRegenEnabled] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);

  const registerSidebarAction = useCallback((opts: SidebarActionOpts) => {
    sidebarGenerateFnRef.current = opts.fn;
    sidebarStopFnRef.current = opts.stopFn ?? null;
    setSidebarGenerateEnabled(opts.enabled);
    setSidebarGenerateLoading(opts.loading ?? false);
  }, []);

  const fireSidebarGenerate = useCallback(() => {
    sidebarGenerateFnRef.current?.();
  }, []);

  const fireSidebarStop = useCallback(() => {
    sidebarStopFnRef.current?.();
  }, []);

  const registerRegenAction = useCallback((opts: RegenActionOpts) => {
    regenFnRef.current = opts.fn;
    regenStopFnRef.current = opts.stopFn ?? null;
    setHasArtifact(opts.hasArtifact);
    setRegenEnabled(opts.enabled);
    setRegenLoading(opts.loading ?? false);
  }, []);

  const fireRegen = useCallback(() => {
    regenFnRef.current?.();
  }, []);

  const fireRegenStop = useCallback(() => {
    regenStopFnRef.current?.();
  }, []);

  useEffect(() => {
    localStorage.setItem('tk-feature-tree', featureTreeOpen.toString());
  }, [featureTreeOpen]);

  // Persist vehicle nav on every change so a refresh rehydrates the exact
  // domain/sub-tab the user was on (the fix for JAWS not reopening, issue #51).
  useEffect(() => {
    try {
      localStorage.setItem(
        VEHICLE_NAV_KEY,
        JSON.stringify({
          vehicleEnabled,
          textToCadSelected,
          vehicleTab,
          vehicleSubTab,
        }),
      );
    } catch {
      // localStorage may be disabled / quota exceeded — nav just won't persist.
    }
  }, [vehicleEnabled, textToCadSelected, vehicleTab, vehicleSubTab]);

  // Generation timing — persisted so "Generated in" survives refresh/revisit
  const [generationTimes, setGenerationTimes] = useState<
    Record<string, number>
  >(() => {
    try {
      const raw = localStorage.getItem('tk-generation-times');
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      return {};
    }
  });
  const recordGenerationTime = useCallback(
    (messageId: string, seconds: number) => {
      setGenerationTimes((prev) => {
        if (prev[messageId] != null) return prev; // immutable once recorded
        const next = { ...prev, [messageId]: seconds };
        try {
          localStorage.setItem('tk-generation-times', JSON.stringify(next));
        } catch (_) {
          /* quota exceeded — generation times not persisted */
        }
        return next;
      });
    },
    [],
  );

  // Per-tab state capture/restore — use refs to always read current values
  const vehicleEnabledRef = useRef(vehicleEnabled);
  vehicleEnabledRef.current = vehicleEnabled;
  const textToCadSelectedRef = useRef(textToCadSelected);
  textToCadSelectedRef.current = textToCadSelected;
  const vehicleTabRef = useRef(vehicleTab);
  vehicleTabRef.current = vehicleTab;
  const vehicleSubTabRef = useRef(vehicleSubTab);
  vehicleSubTabRef.current = vehicleSubTab;

  const captureTabState = useCallback(
    (): TabToolbarState => ({
      vehicleEnabled: vehicleEnabledRef.current,
      textToCadSelected: textToCadSelectedRef.current,
      vehicleTab: vehicleTabRef.current,
      vehicleSubTab: vehicleSubTabRef.current,
    }),
    [],
  );

  const loadTabState = useCallback((state: TabToolbarState) => {
    setVehicleEnabled(state.vehicleEnabled);
    setTextToCadSelected(state.textToCadSelected);
    setVehicleTab(state.vehicleTab);
    setVehicleSubTab(state.vehicleSubTab);
  }, []);

  // Memoize the (very wide ~40-field) context value so a setter call on any one
  // field doesn't hand every consumer a brand-new object identity and re-render
  // the whole tree. Deps list every state value + the derived `isGenerating`;
  // stable setters/callbacks are listed further down only to satisfy
  // exhaustive-deps (see comment there).
  const value = useMemo(
    () => ({
      vehicleEnabled,
      setVehicleEnabled,
      textToCadSelected,
      setTextToCadSelected,
      vehicleTab,
      setVehicleTab,
      vehicleSubTab,
      setVehicleSubTab,
      cadMode,
      setCadMode,
      viewerTheme,
      setViewerTheme,
      tubeframeParamsReady,
      setTubeframeParamsReady,
      tubeframeParams,
      setTubeframeParams,
      supinInletType,
      setSupinInletType,
      supinParamsReady,
      setSupinParamsReady,
      supinParams,
      setSupinParams,
      suspensionKinematicsParamsReady,
      setSuspensionKinematicsParamsReady,
      suspensionKinematicsParams,
      setSuspensionKinematicsParams,
      implicitPrimitive,
      setImplicitPrimitive,
      implicitPrimitiveParams,
      setImplicitPrimitiveParams,
      resetImplicitPrimitiveParams,
      featureTreeOpen,
      setFeatureTreeOpen,
      model,
      setModel,
      outputFormat,
      setOutputFormat,
      type,
      setType,
      imageUploaded,
      setImageUploaded,
      downloadSlot,
      setDownloadSlot,
      downloadFormats,
      registerDownload,
      jawsSlot,
      setJawsSlot,
      domainParamsVersion,
      bumpDomainParams,
      sidebarGenerateEnabled,
      sidebarGenerateLoading,
      registerSidebarAction,
      fireSidebarGenerate,
      fireSidebarStop,
      isGenerating: sidebarGenerateLoading || regenLoading,
      hasArtifact,
      regenEnabled,
      regenLoading,
      registerRegenAction,
      fireRegen,
      fireRegenStop,
      generationTimes,
      recordGenerationTime,
      captureTabState,
      loadTabState,
    }),
    [
      vehicleEnabled,
      textToCadSelected,
      vehicleTab,
      vehicleSubTab,
      cadMode,
      viewerTheme,
      tubeframeParamsReady,
      tubeframeParams,
      supinInletType,
      supinParamsReady,
      supinParams,
      suspensionKinematicsParamsReady,
      suspensionKinematicsParams,
      implicitPrimitive,
      implicitPrimitiveParams,
      featureTreeOpen,
      model,
      outputFormat,
      type,
      imageUploaded,
      downloadSlot,
      downloadFormats,
      registerDownload,
      jawsSlot,
      domainParamsVersion,
      sidebarGenerateEnabled,
      sidebarGenerateLoading,
      hasArtifact,
      regenEnabled,
      regenLoading,
      generationTimes,
      // Stable identities (useState setters + useCallback actions) — listed
      // here so the exhaustive-deps lint is satisfied without disabling it.
      setOutputFormat,
      resetImplicitPrimitiveParams,
      bumpDomainParams,
      registerSidebarAction,
      fireSidebarGenerate,
      fireSidebarStop,
      registerRegenAction,
      fireRegen,
      fireRegenStop,
      recordGenerationTime,
      captureTabState,
      loadTabState,
    ],
  );

  return (
    <HomepageContext.Provider value={value}>
      {children}
    </HomepageContext.Provider>
  );
}
